import {call, put, takeLatest} from "redux-saga/effects";
import {API} from "../../../api/api";
import {ToasterService} from "../../../services/toaster/toaster.service";
import {checkAuthorization, checkPermission} from "../../../utils/Authorization/authorization";
import {AuthorizationServiceTypes} from "../../../utils/Authorization/authorizationTypes";
import {configurationsAPI} from "../../configurations/api/api";
import {HallsActionTypes} from "../actions/actionTypes";
import {hallsAPI} from "../api/api";
import {addGameNamesToRTPProfiles} from "../../configurations/components/Utils/rtp-profiles";

function* getServers(action) {
    try {
        yield put({
            type: HallsActionTypes.GET_SERVERS_INFO,
            payload: {
                hall_id: action.payload.hallId
            }
        });

        const request = yield call(() =>
            hallsAPI.getServers(
                {
                    hallId: action.payload.hallId,
                    search: action.payload.search,
                    status: action.payload.status,
                    version: action.payload.version
                })
        );

        const serverIDs = request.data.results
            .map(servers => servers.id)
            .join(",");

        const licenses =
            checkAuthorization({
                service: AuthorizationServiceTypes.LICENSES
            }) && request.data.results.length > 0
                ? yield* _getLicensesDataFromId(serverIDs)
                : {
                    data: {
                        results: []
                    }
                };

        let serversList = [...request.data.results];
        const configs = checkPermission("upload_configuration_remotely")
            ? yield _getLatestConfigs(serversList)
            : []

        serversList = serversList.map(server => {
            if (licenses.data["results"] && licenses.data["results"].length > 0) {
                const serverLicense = licenses.data["results"].find(
                    license => license.server_license_state.server_id == server.id
                );
                if (serverLicense != undefined) {
                    server.license = serverLicense;
                }
            }
            server['config'] = configs.filter(
                config => config.server_id == server.id
            )[0];
            return server;
        });

        yield put({
            type: HallsActionTypes.GET_SERVERS_SUCCESS,
            payload: {
                servers: serversList
            }
        });
    } catch (e) {
        API.handleError(e.response, "Error getting servers.");

        yield put({
            type: HallsActionTypes.GET_SERVERS_FAIL,
            message: e.message
        });
    }
}

/**
 * Returns the licenses that match the serverIds provided.
 * @param serverIds
 */
function* _getLicensesDataFromId(serverIds: any) {
    let licenses = {data: []};
    licenses = yield call(() =>
        hallsAPI.getLicenses({filter: "server_id in (" + serverIds + ",)"})
    );
    return licenses;
}

function* _getLatestConfigs(servers) {
    let configs = [];
    for (let i = 0; i < servers.length; i++) {
        if (servers[i].latest_config_id && servers[i].is_online) {
            let config = yield call(() => configurationsAPI.getConfiguration(
                {
                    serverId: servers[i].id,
                    config: servers[i].latest_config_id
                }))
            configs.push({
                server_id: servers[i].id,
                hall: config.data['hall_id'],
                id: config.data['settings']['id'],
                version: config.data['settings']['version'],
                created_on: config.data['settings']['created_on'],
                auto_session_enabled: config.data['settings']['settings']['settings']['auto_session_enabled'],
                game_rtp_profiles: addGameNamesToRTPProfiles(
                    config.data['settings']['settings']['game_rtp_profiles'] !== undefined
                        ? config.data['settings']['settings']['game_rtp_profiles']
                        : config.data['settings']['settings']['rtp_profiles'],
                    config.data['settings']['settings']['game_settings']
                )
            })
        }
    }
    return configs
}

export function* getServersWatcher() {
    yield takeLatest(HallsActionTypes.GET_SERVERS, getServers);
}

function* getServer(action) {
    try {
        const request = yield call(() =>
            hallsAPI.getServer({server: action.payload.serverId})
        );

        yield put({
            type: HallsActionTypes.GET_SERVER_SUCCESS,
            payload: request.data
        });
    } catch (e) {
        API.handleError(e.response, "Error getting servers.");

        yield put({
            type: HallsActionTypes.GET_SERVER_FAIL,
            message: e.message
        });
    }
}

function* toggleServerActiveState(action) {
    const serverId = action.payload.serverId;
    try {
        let status = yield call(() => hallsAPI.activateServer(serverId));

        if (status.status == 202) {
            yield put({
                type: HallsActionTypes.TOGGLE_SERVER_ACTIVE_SUCCESS,
                payload: {
                    serverId: serverId
                }
            });
        } else {
            ToasterService.errorToast("Error changing server status.");
        }
    } catch (e) {
        API.handleError(e.response, "Error changing server status.");

        yield put({
            type: HallsActionTypes.TOGGLE_SERVER_ACTIVE_FAIL,
            message: e.message
        });
    }
}

export function* getServerWatcher() {
    yield takeLatest(HallsActionTypes.GET_SERVER, getServer);
    yield takeLatest(
        HallsActionTypes.TOGGLE_SERVER_ACTIVE,
        toggleServerActiveState
    );
    yield takeLatest(
        HallsActionTypes.TOGGLE_SERVER_DEBUG_MODE,
        toggleServerDebugMode
    );
    yield takeLatest(
        HallsActionTypes.TOGGLE_SERVER_PERPETUAL_LICENSE,
        toggleServerPerpetualLicense
    );
}

function* getServersInfo(action) {
    const {hall_id} = action.payload;
    try {
        const serversInfo = yield call(() =>
            hallsAPI.getServersInfo({hall_id})
        );

        yield put({
            type: HallsActionTypes.GET_SERVERS_INFO_SUCCESS,
            payload: {
                serversInfo: serversInfo.data
            }
        });
    } catch (e) {
        API.handleError(e.response, "Error get servers info.");

        yield put({
            type: HallsActionTypes.GET_SERVERS_INFO_FAIL,
            message: e.message
        });
    }
}

function* toggleServerDebugMode(action) {
    const serverId = action.payload.serverId;
    try {
        let status = yield call(() => hallsAPI.toggleServerDebugMode(serverId));

        if (status.status == 202) {
            yield put({
                type: HallsActionTypes.TOGGLE_SERVER_DEBUG_MODE_SUCCESS,
                payload: {
                    serverId: serverId
                }
            });
        } else {
            ToasterService.errorToast("Error changing server debug mode.");
        }
    } catch (e) {
        API.handleError(e.response, "Error changing server debug mode.");

        yield put({
            type: HallsActionTypes.TOGGLE_SERVER_DEBUG_MODE_FAIL,
            message: e.message
        });
    }
}

function* toggleServerPerpetualLicense(action) {
    const serverId = action.payload.serverId;
    try {
        let status = yield call(() => hallsAPI.toggleServerPerpetualLicense(serverId));

        if (status.status == 202) {
            yield put({
                type: HallsActionTypes.TOGGLE_SERVER_PERPETUAL_LICENSE_SUCCESS,
                payload: {
                    serverId: serverId
                }
            });
        } else {
            ToasterService.errorToast("Error changing server licence.");
        }
    } catch (e) {
        API.handleError(e.response, "Error changing server licence.");

        yield put({
            type: HallsActionTypes.TOGGLE_SERVER_PERPETUAL_LICENSE_FAIL,
            message: e.message
        });
    }
}

export function* getServersInfoWatcher() {
    yield takeLatest(HallsActionTypes.GET_SERVERS_INFO, getServersInfo);
}

function* getProductionServer() {
    try {
        const request = yield call(() =>
            hallsAPI.getProductionServer()
        );

        yield put({
            type: HallsActionTypes.GET_PRODUCTION_INFO_SUCCESS,
            payload: request.data
        });
    } catch (e) {
        API.handleError(e.response, "Error getting production info.");

        yield put({
            type: HallsActionTypes.GET_PRODUCTION_INFO_FAIL,
            message: e.message
        });
    }
}

export function* getProductionServerWatcher() {
    yield takeLatest(HallsActionTypes.GET_PRODUCTION_INFO, getProductionServer);
}
